import {
  CrewKindSchema,
  MissionTypeSchema,
  WeatherSchema,
} from '@trajectory/contracts';
import { NumberField, SelectField, TextField } from '@trajectory/ui';
import {
  FileJson,
  FileSpreadsheet,
  FileText,
  History,
  Loader2,
  RotateCcw,
} from 'lucide-react';
import { useState } from 'react';
import { PanelLearningBanner } from '@/components/PanelLearningBanner';
import { TermHelp } from '@/components/TermHelp';
import { useCatalogLaunchSites } from '@/hooks/useCatalog';
import { useMissionVersions, useRestoreMissionVersion } from '@/hooks/useMissionVersions';
import { downloadMissionExport } from '@/lib/download';
import {
  destinationsForMissionType,
  suggestedDestination,
} from '@/lib/mission-wizard';
import {
  maneuverKindDescription,
  maneuverKindOptions,
} from './OrbitEditorPanel';
import { loadMissionRecord, updateManeuver, useMissionStore } from '@/stores/missionStore';
import { updateStage, useRocketStore } from '@/stores/rocketStore';
import { useSelectionStore } from '@/stores/selectionStore';
import { useUiStore } from '@/stores/uiStore';

const enumOptions = (values: readonly string[]) => values.map((value) => ({ value, label: value }));

function MissionActions({ missionId, missionName }: { missionId: string; missionName: string }) {
  const [showHistory, setShowHistory] = useState(false);
  const [exporting, setExporting] = useState<'json' | 'csv' | 'pdf' | null>(null);
  const versions = useMissionVersions(showHistory ? missionId : null);
  const restore = useRestoreMissionVersion();

  const handleExport = async (format: 'json' | 'csv' | 'pdf') => {
    setExporting(format);
    try {
      await downloadMissionExport(
        missionId,
        format,
        `${missionName.replace(/\s+/g, '-').toLowerCase()}.${format}`,
      );
      useUiStore.getState().notify({
        kind: 'success',
        title: `${format.toUpperCase()} export ready`,
        message: 'The download contains a snapshot; no mission data was changed.',
      });
    } catch (error) {
      useUiStore
        .getState()
        .notify({
          kind: 'danger',
          title: 'Export failed',
          message: error instanceof Error ? error.message : String(error),
        });
    } finally {
      setExporting(null);
    }
  };

  const handleRestore = async (version: number) => {
    if (
      !window.confirm(`Restore version ${version}? This creates a new version from that snapshot.`)
    )
      return;
    try {
      const restored = await restore.mutateAsync({ missionId, version });
      loadMissionRecord(restored);
      useUiStore.getState().notify({
        kind: 'success',
        title: `Restored version ${version}`,
        message: 'A new current version was created; the historical snapshot remains unchanged.',
      });
    } catch (error) {
      useUiStore.getState().notify({
        kind: 'danger',
        title: `Could not restore version ${version}`,
        message: error instanceof Error ? error.message : String(error),
      });
    }
  };

  return (
    <div className="mt-2 flex flex-col gap-2 border-t border-border pt-3">
      <p className="text-xs font-medium text-muted">Export</p>
      <div className="flex gap-1.5">
        <button
          type="button"
          onClick={() => void handleExport('json')}
          disabled={exporting !== null}
          title="Download a complete machine-readable mission snapshot without changing the mission"
          className="flex flex-1 items-center justify-center gap-1 rounded-control border border-border px-2 py-1 text-xs text-muted hover:text-text"
        >
          {exporting === 'json' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileJson className="h-3.5 w-3.5" />} JSON
        </button>
        <button
          type="button"
          onClick={() => void handleExport('csv')}
          disabled={exporting !== null}
          title="Download mission data in spreadsheet-friendly CSV format without changing the mission"
          className="flex flex-1 items-center justify-center gap-1 rounded-control border border-border px-2 py-1 text-xs text-muted hover:text-text"
        >
          {exporting === 'csv' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="h-3.5 w-3.5" />} CSV
        </button>
        <button
          type="button"
          onClick={() => void handleExport('pdf')}
          disabled={exporting !== null}
          title="Download a reader-friendly mission summary without changing the mission"
          className="flex flex-1 items-center justify-center gap-1 rounded-control border border-border px-2 py-1 text-xs text-muted hover:text-text"
        >
          {exporting === 'pdf' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} PDF
        </button>
      </div>

      <button
        type="button"
        onClick={() => setShowHistory((current) => !current)}
        className="flex items-center gap-1.5 text-xs text-muted hover:text-text"
      >
        <History className="h-3.5 w-3.5" /> Version history
      </button>
      {showHistory && (
        <div className="flex flex-col gap-1">
          {versions.isLoading && <p className="text-xs text-faint">Loading…</p>}
          {versions.isError && (
            <p role="alert" className="rounded-control bg-danger/10 p-2 text-xs text-danger">
              Could not load version history:{' '}
              {versions.error instanceof Error
                ? versions.error.message
                : String(versions.error)}
            </p>
          )}
          {versions.data?.map((entry) => (
            <div
              key={entry.id}
              className="flex items-center justify-between gap-2 rounded-control border border-border px-2 py-1 text-xs"
            >
              <span className="text-muted">
                v{entry.version} · {entry.label ?? 'Autosave'} ·{' '}
                {new Date(entry.createdAt).toLocaleString()}
              </span>
              <button
                type="button"
                onClick={() => void handleRestore(entry.version)}
                title="Restore this version"
                className="shrink-0 rounded-control p-1 text-faint hover:text-text"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MissionInspector() {
  const missionId = useMissionStore((state) => state.id);
  const document = useMissionStore((state) => state.document);
  const patch = useMissionStore((state) => state.patch);
  const launchSites = useCatalogLaunchSites();

  if (!document || !missionId) return null;

  const weather = document.weather ?? WeatherSchema.parse({});

  return (
    <div className="flex flex-col gap-2 p-3">
      <TextField label="Name" value={document.name} onChange={(name) => patch({ name })} />
      <TextField
        label="Description"
        value={document.description}
        onChange={(description) => patch({ description })}
      />
      <SelectField
        label="Mission type"
        value={document.missionType}
        options={enumOptions(MissionTypeSchema.options)}
        onChange={(missionTypeValue) => {
          const missionType = missionTypeValue as typeof document.missionType;
          const destinations = destinationsForMissionType(missionType);
          patch({
            missionType,
            destinationBody: (
              destinations as readonly typeof document.destinationBody[]
            ).includes(document.destinationBody)
              ? document.destinationBody
              : suggestedDestination(missionType),
          });
        }}
      />
      <label className="flex flex-col gap-1 text-xs text-muted">
        Launch date and time (UTC)
        <input
          type="datetime-local"
          value={document.launchAt.slice(0, 16)}
          onChange={(event) => {
            const instant = new Date(`${event.target.value}:00Z`);
            if (!Number.isNaN(instant.getTime())) patch({ launchAt: instant.toISOString() });
          }}
          className="rounded-control border border-border bg-surface px-2 py-1.5 text-sm text-text"
        />
      </label>
      <SelectField
        label="Destination body"
        value={document.destinationBody}
        options={enumOptions(destinationsForMissionType(document.missionType))}
        onChange={(destinationBody) =>
          patch({ destinationBody: destinationBody as typeof document.destinationBody })
        }
      />
      <SelectField
        label="Launch site"
        value={document.launchSiteId}
        options={(launchSites.data ?? []).map((site) => ({ value: site.id, label: site.name }))}
        onChange={(launchSiteId) => patch({ launchSiteId })}
      />
      <NumberField
        label={<TermHelp termId="payload">Payload mass (kg)</TermHelp>}
        value={document.payloadMassKg}
        min={0}
        hint="Spacecraft, instruments, cargo, and crew delivered by the launch vehicle."
        onChange={(payloadMassKg) => patch({ payloadMassKg })}
      />
      <SelectField
        label="Crew"
        value={document.crewKind}
        options={enumOptions(CrewKindSchema.options)}
        onChange={(crewKindValue) => {
          const crewKind = crewKindValue as typeof document.crewKind;
          patch({
            crewKind,
            crewCount:
              crewKind === 'crew' ? Math.max(1, document.crewCount) : 0,
          });
        }}
      />
      {document.crewKind === 'crew' && (
        <NumberField
          label="Crew count"
          value={document.crewCount}
          min={1}
          max={20}
          step={1}
          onChange={(crewCount) => patch({ crewCount: Math.round(crewCount) })}
        />
      )}
      <details
        className="rounded-panel border border-border bg-surface-raised p-2"
        open={document.weather !== undefined}
      >
        <summary className="cursor-pointer text-xs font-medium text-muted">
          Weather conditions
        </summary>
        <div className="mt-2 flex flex-col gap-2">
          <NumberField
            label="Surface temperature (K)"
            value={weather.surfaceTemperatureK}
            min={180}
            max={340}
            onChange={(surfaceTemperatureK) =>
              patch({ weather: { ...weather, surfaceTemperatureK } })
            }
          />
          <NumberField
            label="Surface pressure (Pa)"
            value={weather.surfacePressurePa}
            min={50_000}
            max={110_000}
            onChange={(surfacePressurePa) => patch({ weather: { ...weather, surfacePressurePa } })}
          />
          <NumberField
            label="Surface wind speed (m/s)"
            value={weather.surfaceWindSpeed}
            min={0}
            max={60}
            onChange={(surfaceWindSpeed) => patch({ weather: { ...weather, surfaceWindSpeed } })}
          />
          <NumberField
            label="Wind direction (°)"
            value={weather.surfaceWindDirectionDeg}
            min={0}
            max={360}
            onChange={(surfaceWindDirectionDeg) =>
              patch({ weather: { ...weather, surfaceWindDirectionDeg } })
            }
          />
          <NumberField
            label="Jet stream speed (m/s)"
            value={weather.jetStreamSpeed}
            min={0}
            max={120}
            onChange={(jetStreamSpeed) => patch({ weather: { ...weather, jetStreamSpeed } })}
          />
          <NumberField
            label="Jet stream altitude (m)"
            value={weather.jetStreamAltitudeMeters}
            min={5000}
            max={20_000}
            onChange={(jetStreamAltitudeMeters) =>
              patch({ weather: { ...weather, jetStreamAltitudeMeters } })
            }
          />
          <NumberField
            label="Gust amplitude (m/s)"
            value={weather.gustAmplitude}
            min={0}
            max={30}
            onChange={(gustAmplitude) => patch({ weather: { ...weather, gustAmplitude } })}
          />
          <NumberField
            label="Relative humidity"
            value={weather.relativeHumidity}
            min={0}
            max={1}
            step={0.05}
            onChange={(relativeHumidity) => patch({ weather: { ...weather, relativeHumidity } })}
          />
          <NumberField
            label="Solar activity multiplier"
            value={weather.solarActivity}
            min={0.25}
            max={4}
            step={0.05}
            onChange={(solarActivity) => patch({ weather: { ...weather, solarActivity } })}
          />
          <label className="flex items-center gap-2 text-xs text-muted">
            <input
              type="checkbox"
              checked={weather.lightningWithinConstraintRadius}
              onChange={(event) =>
                patch({
                  weather: { ...weather, lightningWithinConstraintRadius: event.target.checked },
                })
              }
              className="accent-accent"
            />
            Lightning within constraint radius
          </label>
        </div>
      </details>
      <TextField
        label="Tags (comma-separated)"
        value={document.tags.join(', ')}
        onChange={(value) =>
          patch({
            tags: value
              .split(',')
              .map((tag) => tag.trim())
              .filter(Boolean),
          })
        }
      />
      <MissionActions missionId={missionId} missionName={document.name} />
    </div>
  );
}

function StageInspector({ stageId }: { stageId: string }) {
  const stage = useRocketStore((state) =>
    state.document?.stages.find((candidate) => candidate.id === stageId),
  );
  if (!stage) return <p className="p-3 text-xs text-faint">This stage no longer exists.</p>;

  return (
    <div className="flex flex-col gap-2 p-3">
      <NumberField
        label="Tank mass (kg)"
        value={stage.tankMassKg}
        min={0}
        onChange={(tankMassKg) => updateStage(stageId, { tankMassKg })}
      />
      <NumberField
        label="Tank volume (m³)"
        value={stage.tankVolumeCubicMeters ?? 1}
        min={0.001}
        onChange={(tankVolumeCubicMeters) => updateStage(stageId, { tankVolumeCubicMeters })}
      />
      <NumberField
        label="Diameter (m)"
        value={stage.diameterMeters}
        min={0.1}
        onChange={(diameterMeters) => updateStage(stageId, { diameterMeters })}
      />
      <NumberField
        label="Separation delay (s)"
        value={stage.separationDelaySeconds}
        min={0}
        onChange={(separationDelaySeconds) => updateStage(stageId, { separationDelaySeconds })}
      />
      <NumberField
        label="Nominal throttle"
        value={stage.nominalThrottle ?? 1}
        min={0}
        max={1.5}
        onChange={(nominalThrottle) => updateStage(stageId, { nominalThrottle })}
      />
      <details
        className="rounded-panel border border-border bg-surface-raised p-2"
        open={Boolean(stage.boosters)}
      >
        <summary className="cursor-pointer text-xs font-medium text-muted">
          Booster configuration
        </summary>
        <div className="mt-2 flex flex-col gap-2">
          {!stage.boosters ? (
            <button
              type="button"
              onClick={() =>
                updateStage(stageId, {
                  boosters: {
                    count: 2,
                    engine: stage.engine,
                    enginesPerBooster: 1,
                    structuralMassKg: 1000,
                    propellantMassKg: 5000,
                    referenceAreaSqm: 3,
                  },
                })
              }
              className="rounded-control border border-border px-2 py-1 text-xs text-muted hover:text-text"
            >
              Add boosters
            </button>
          ) : (
            <>
              <NumberField
                label="Booster count"
                value={stage.boosters.count}
                min={0}
                max={16}
                step={1}
                onChange={(count) =>
                  updateStage(stageId, {
                    boosters: { ...stage.boosters!, count: Math.round(count) },
                  })
                }
              />
              <NumberField
                label="Engines per booster"
                value={stage.boosters.enginesPerBooster}
                min={1}
                max={16}
                step={1}
                onChange={(enginesPerBooster) =>
                  updateStage(stageId, {
                    boosters: {
                      ...stage.boosters!,
                      enginesPerBooster: Math.round(enginesPerBooster),
                    },
                  })
                }
              />
              <NumberField
                label="Booster structure (kg)"
                value={stage.boosters.structuralMassKg}
                min={0}
                onChange={(structuralMassKg) =>
                  updateStage(stageId, { boosters: { ...stage.boosters!, structuralMassKg } })
                }
              />
              <NumberField
                label="Booster propellant (kg)"
                value={stage.boosters.propellantMassKg}
                min={0}
                onChange={(propellantMassKg) =>
                  updateStage(stageId, { boosters: { ...stage.boosters!, propellantMassKg } })
                }
              />
              <NumberField
                label="Booster reference area (m²)"
                value={stage.boosters.referenceAreaSqm}
                min={0}
                onChange={(referenceAreaSqm) =>
                  updateStage(stageId, { boosters: { ...stage.boosters!, referenceAreaSqm } })
                }
              />
              <button
                type="button"
                onClick={() => updateStage(stageId, { boosters: undefined })}
                className="rounded-control border border-border px-2 py-1 text-xs text-danger"
              >
                Remove boosters
              </button>
            </>
          )}
        </div>
      </details>
      <div className="rounded-panel border border-border bg-surface-raised p-2 text-xs text-muted">
        <p className="mb-1 font-medium text-text">{stage.engine.name}</p>
        <p>
          {(stage.engine.thrustVacuumNewtons / 1000).toFixed(0)} kN vac ·{' '}
          {stage.engine.ispVacuumSeconds.toFixed(0)}s Isp · {stage.engine.propellant}
        </p>
        {stage.boosters && (
          <p className="mt-1">
            {stage.boosters.count} × {stage.boosters.engine.name} booster
            {stage.boosters.count === 1 ? '' : 's'}
          </p>
        )}
      </div>
    </div>
  );
}

function ManeuverInspector({ maneuverId }: { maneuverId: string }) {
  const maneuver = useMissionStore((state) =>
    state.document?.maneuvers.find((candidate) => candidate.id === maneuverId),
  );
  if (!maneuver) return <p className="p-3 text-xs text-faint">This maneuver no longer exists.</p>;

  return (
    <div className="flex flex-col gap-2 p-3">
      <TextField
        label="Name"
        value={maneuver.name}
        onChange={(name) => updateManeuver(maneuverId, { name })}
      />
      <SelectField
        label="Kind"
        value={maneuver.kind}
        options={maneuverKindOptions()}
        hint={maneuverKindDescription(maneuver.kind)}
        onChange={(kind) => updateManeuver(maneuverId, { kind: kind as typeof maneuver.kind })}
      />
      <NumberField
        label="Start time (s)"
        value={maneuver.startTimeSeconds}
        min={0}
        onChange={(startTimeSeconds) => updateManeuver(maneuverId, { startTimeSeconds })}
      />
      <NumberField
        label={<TermHelp termId="delta-v">Delta-V (m/s)</TermHelp>}
        value={maneuver.deltaVMetersPerSecond}
        min={0}
        hint="Ideal impulsive velocity change assigned to this maneuver."
        onChange={(deltaVMetersPerSecond) => updateManeuver(maneuverId, { deltaVMetersPerSecond })}
      />
      <TextField
        label="Notes"
        value={maneuver.notes ?? ''}
        onChange={(notes) => updateManeuver(maneuverId, { notes })}
      />
    </div>
  );
}

export function InspectorPanel() {
  const selection = useSelectionStore((state) => state.selection);

  if (!selection) {
    return (
      <div className="flex h-full items-center justify-center p-4 text-center text-xs text-faint">
        Click a mission, stage or maneuver to edit its full field set here.
      </div>
    );
  }

  return (
    <div
      data-testid="inspector-scroll"
      className="h-full min-h-0 overflow-y-auto overscroll-contain"
    >
      <div className="p-3 pb-0">
        <PanelLearningBanner panelId="inspector" />
      </div>
      {selection.kind === 'mission' ? (
        <MissionInspector />
      ) : selection.kind === 'stage' ? (
        <StageInspector stageId={selection.stageId} />
      ) : (
        <ManeuverInspector maneuverId={selection.maneuverId} />
      )}
    </div>
  );
}
